import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Full-screen logo intro with loading-aware behavior.
 *
 * Flow:
 *  1. Show logo full-screen with entrance animation.
 *  2. After 700ms (INTRO_MIN_SPIN), if the window `load` event has not yet fired,
 *     start a gentle continuous rotation to signal "still loading".
 *  3. Once the window has loaded (or after INTRO_MAX_MS as safety), fade out and
 *     unlock body scroll.
 */
const INTRO_MIN_MS = 500;    // minimum time the intro is visible (Core Web Vitals friendly)
const SPIN_AFTER_MS = 400;   // start rotating if still visible after this
const INTRO_MAX_MS = 3000;   // hard cap — never lock the user out

export default function LogoIntro() {
  const [visible, setVisible] = useState(true);
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Lock scroll while intro is showing
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.scrollTo(0, 0);

    if (reduce) {
      const t = setTimeout(() => setVisible(false), 400);
      return () => { clearTimeout(t); document.body.style.overflow = prevOverflow; };
    }

    const started = performance.now();
    let hideTimer;
    let spinTimer;

    // Start spinning if load is slow
    spinTimer = setTimeout(() => {
      if (document.readyState !== "complete") setSpinning(true);
    }, SPIN_AFTER_MS);

    const scheduleHide = () => {
      if (hideTimer) return;
      const elapsed = performance.now() - started;
      const wait = Math.max(0, INTRO_MIN_MS - elapsed);
      hideTimer = setTimeout(() => setVisible(false), wait);
    };

    if (document.readyState === "complete") {
      scheduleHide();
    } else {
      window.addEventListener("load", scheduleHide, { once: true });
    }

    // Absolute safety cap
    const cap = setTimeout(() => setVisible(false), INTRO_MAX_MS);

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(spinTimer);
      clearTimeout(cap);
      window.removeEventListener("load", scheduleHide);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return (
    <AnimatePresence onExitComplete={() => { document.body.style.overflow = ""; }}>
      {visible && (
        <motion.div
          key="logo-intro"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0B0D10]"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.55, ease: "easeInOut" } }}
          data-testid="logo-intro"
          aria-hidden="true"
        >
          {/* Ambient blue glow */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(0,102,255,0.28) 0%, rgba(0,102,255,0.08) 32%, rgba(11,13,16,0) 65%)",
            }}
          />

          {/* Logo wrapper — separates entrance animation from rotation loop */}
          <motion.div
            className="relative"
            initial={{ scale: 0.55, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              animate={spinning ? { rotate: 360 } : { rotate: 0 }}
              transition={
                spinning
                  ? { repeat: Infinity, duration: 1.8, ease: "linear" }
                  : { duration: 0.4, ease: "easeOut" }
              }
              className="relative"
            >
              {/* White rounded card holding the source logo at near-native pixels for max sharpness */}
              <div
                className="rounded-[32px] bg-white px-10 py-8 shadow-[0_30px_80px_rgba(0,102,255,0.35)]"
                style={{ willChange: "transform" }}
              >
                <img
                  src="/myyntidrive-logo.png?v=7"
                  srcSet="/myyntidrive-logo.png?v=7 1x, /myyntidrive-logo@2x.png?v=7 2x"
                  alt="MyyntiDrive"
                  className="block h-auto w-[240px] sm:w-[280px] md:w-[320px] select-none"
                  width="217"
                  height="128"
                  draggable={false}
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Loading hint */}
          <motion.p
            className="absolute bottom-10 left-0 right-0 text-center text-xs font-mono uppercase tracking-[0.4em] text-[#00E5FF]/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {spinning ? "Ladataan..." : "Ammattimaiset verkkosivut alk. 150 €"}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
